import { Select, Text } from "@mantine/core";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { useState } from "react";
import {
  CancellationState,
  Paragraph6EEGCreditResponseState
} from "../../../../../types/api.types";
import { THEME_VARS } from "../../../../../utils/constants";
import { useParagraph6CancellationMutations } from "../../../hooks/useParagraph6CancellationMutations";
import { useParagraph6CreditMutations } from "../../../hooks/useParagraph6CreditMutations";
import { useShowParagraph6CreditsAlerts } from "../../../hooks/useShowParagraph6CreditsAlerts";
import {
  PARAGRAPH_6_CANCELLATION_STATE_OPTIONS,
  PARAGRAPH_6_INVOICE_STATE_OPTIONS
} from "../../../Paragraph6.constants";
import type { Paragraph6FinancialDocument } from "../../../Paragraph6.types";
import { FinancialDocumentType } from "../../../Paragraph6.types";
import { Paragraph6PaymentDateModal } from "../../Paragraph6PaymentDateModal/Paragraph6PaymentDateModal";

const uniqueFilterValues = Array.from(
  new Set([
    ...Object.values(Paragraph6EEGCreditResponseState),
    ...Object.values(CancellationState)
  ])
);
const uniqueFilterValuesDict = {
  [Paragraph6EEGCreditResponseState.ready_to_send]: "Versandfertig",
  [Paragraph6EEGCreditResponseState.sent]: "Versendet",
  [Paragraph6EEGCreditResponseState.paid]: "Bezahlt",
  [Paragraph6EEGCreditResponseState.cancelled]: "Storniert",
  [CancellationState.received]: "Erhalten"
};

function getStateColumn(variantId: number) {
  return {
    header: "Status",
    accessorKey: "state",
    Cell: ({ cell, row }) => {
      const [showConfirmationModal, setShowConfirmationModal] = useState(false);

      const { paragraph6CreditUpdateStatusMutation } =
        useParagraph6CreditMutations(variantId);

      const { showParagraph6CreditsAlerts } = useShowParagraph6CreditsAlerts();

      const { paragraph6CancellationUpdateMutation } =
        useParagraph6CancellationMutations(variantId);

      function handleCreditStateChange(
        newValue: Paragraph6EEGCreditResponseState
      ) {
        if (newValue === Paragraph6EEGCreditResponseState.paid) {
          setShowConfirmationModal(true);
        } else {
          paragraph6CreditUpdateStatusMutation.mutate(
            {
              creditId: row.original.id,
              payload: { state: newValue }
            },
            {
              onSuccess: (res) => {
                showParagraph6CreditsAlerts({
                  todoAlertState: res.data.todo_alert_state
                });
              }
            }
          );
        }
      }

      function handleModalClose() {
        setShowConfirmationModal(false);
      }

      const initialPaymentDate = row.original.date_of_payment ?? undefined;
      const cellValue = String(cell.getValue());

      const color =
        cellValue === CancellationState.ready_to_send
          ? THEME_VARS.info[4]
          : cellValue === CancellationState.sent
            ? THEME_VARS.warning[4]
            : "#83DBA4";

      const selectColorStyles = {
        input: {
          backgroundColor: color,
          borderColor: color,
          color: "var(--mantine-color-black)"
        },
        section: { backgroundColor: color }
      };

      return (
        <>
          <Paragraph6PaymentDateModal
            creditId={row.original.id}
            initialPaymentDate={initialPaymentDate}
            isOpen={showConfirmationModal}
            variantId={variantId}
            onClose={handleModalClose}
          />
          {row.original.type == FinancialDocumentType.Cancellation ? (
            <Select
              chevronColor={THEME_VARS.neutral[7]}
              classNames={{ input: "invoice-state-select" }}
              data={PARAGRAPH_6_CANCELLATION_STATE_OPTIONS}
              fw={500}
              placeholder="Status auswählen"
              required
              styles={selectColorStyles}
              value={cellValue}
              onChange={(newValue) =>
                paragraph6CancellationUpdateMutation.mutate({
                  cancellation: row.original,
                  payload: { state: newValue as CancellationState }
                })
              }
            />
          ) : row.original.state ===
            Paragraph6EEGCreditResponseState.cancelled ? (
            <Text className="invoice-state-cancelled" fw={500}>
              Storniert
            </Text>
          ) : (
            <Select
              chevronColor={THEME_VARS.neutral[7]}
              classNames={{ input: "invoice-state-select" }}
              data={PARAGRAPH_6_INVOICE_STATE_OPTIONS}
              fw={500}
              placeholder="Status auswählen"
              required
              styles={selectColorStyles}
              value={cellValue}
              onChange={(newValue) =>
                handleCreditStateChange(
                  newValue as Paragraph6EEGCreditResponseState
                )
              }
            />
          )}
        </>
      );
    },
    size: 210,
    enableGlobalFilter: false,
    enableResizing: false,
    filterVariant: "multi-select",
    mantineFilterMultiSelectProps: () => ({
      data: uniqueFilterValues.map((value) => ({
        value: value,
        label: uniqueFilterValuesDict[value]
      }))
    })
  } satisfies MRT_ColumnDef<Paragraph6FinancialDocument>;
}

export { getStateColumn };
